import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Comment,
  Score,
  Analytics
} from '@mui/icons-material';
import { baseUrl, config, requestID } from '../common/utils';
import axios from 'axios';
import { IonContent, IonPage } from '@ionic/react';

const FeedbackReport = ({ token }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState({ table: true });
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const configWithAuth = {
    ...config,
    headers: {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    }
  };

  const fetchReport = async () => {
    try {
      setLoading((prev) => ({ ...prev, table: true }));
      const payload = { requestID: requestID() };
      
      const res = await axios.post(
        `${baseUrl()}performance/report`,
        payload,
        configWithAuth
      );

      if (res.data.success) {
        setReportData(res.data);
      }
    } catch (error) {
      setError('Failed to fetch performance report');
      console.error('Error:', error);
    } finally {
      setLoading((prev) => ({ ...prev, table: false }));
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  if (loading.table) {
    return (
      <IonPage>
        <IonContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <IonContent>
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 1200, mx: 'auto' }}>
          <Typography variant="h4" gutterBottom sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 3
          }}>
            <Assessment /> Performance Report
          </Typography>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, bgcolor: 'primary.light', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Final Score
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {reportData.finalScore}/10
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={reportData.finalScore * 10} 
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, bgcolor: 'secondary.light', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Survey Average
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {reportData.averageSurvey}/10
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={reportData.averageSurvey * 10} 
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, bgcolor: 'success.light', color: 'white' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Qualitative Score
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {reportData.analysis.qualitative_score}/10
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={reportData.analysis.qualitative_score * 10} 
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Analysis Section */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Analytics /> Performance Analysis
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Sentiment:</strong> 
                  <Chip 
                    label={reportData.analysis.sentiment} 
                    color={reportData.analysis.sentiment === 'positive' ? 'success' : 'warning'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Key Themes:</strong>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {reportData.analysis.key_themes.map((theme, index) => (
                    <Chip
                      key={index}
                      label={theme}
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Feedbacks Section */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Comment /> Feedback History
            </Typography>
            {reportData.feedbacks.map((feedback, index) => (
              <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < reportData.feedbacks.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="primary">
                  {feedback.role}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {feedback.text}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(feedback.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* Survey Scores Section */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Score /> Survey Scores
            </Typography>
            <Grid container spacing={2}>
              {reportData.surveyScores.map((score, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {score.category}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {score.score}/10
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={score.score * 10} 
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default FeedbackReport;